import { FC, Fragment } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { RedoxCategoryType } from "DataTypes/PatientManagement.type";
import RedoxApi from "Service/Redox.api";
import CstmAccordion1 from "Components/CstmAccordion";
import { CommonStyle, Loader } from "Components";
import { CatSearchDocType } from "DataTypes/Services/Redox.type";
import { useDispatch } from "react-redux";
import { setAlertData } from "Redux/Screen/Action";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import ImgPath from "Constant/Imgs";
import EhrDocumentApi, { isEhrSourcedDocument } from "Service/EhrDocument.api";
import { documentSubtitle } from "./Format";

const Category: FC<{
  data: RedoxCategoryType[];
  loading: boolean;
  setViewDoc: (data: CatSearchDocType) => void;
}> = ({ data, loading, setViewDoc }) => {
  const dispatch = useDispatch();
  // `ehr` carries the vendor the SERVER reported for this patient. It is what decides whether a
  // document can be pulled back from the EHR -- see resolveVendor in EhrDocument.api: the hardcoded
  // practice-location table it replaces was wrong in every deployed environment (CV-448).
  const { patientInfo, patientOrg, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );

  const handleClick: (
    data: CatSearchDocType,
    action: string,
    silent?: boolean
  ) => Promise<boolean> = async (data, action, silent = false) => {
    const { contentType, documentName, fileType, path, id } = data;
    const payload = {
      contentType,
      // The search index carries no file path, so the id is what the server can resolve a
      // document by; the path is still sent first for the locally uploaded ones that have it.
      filePath: path || id,
      documentName,
    };
    const res = await RedoxApi.DocDownload({
      data: payload,
      id1: patientInfo.id,
    });
    if (!res?.success) return false;
    const binaryResponse = res.data;
    const blob = binaryResponse instanceof Blob
      ? new Blob([await binaryResponse.arrayBuffer()], {
          type: fileType === ".pdf" ? "application/pdf" : contentType,
        })
      : new Blob([binaryResponse], { type: contentType });
    if (!blob.size) {
      if (!silent) {
        dispatch(setAlertData({
          alertMessage: `${documentName} has no readable file content.`,
          alertVariant: "error",
          openAlert: true,
        }));
      }
      return false;
    }
    const link = document.createElement("a");
    link.download = documentName;
    link.href = window.URL.createObjectURL(blob);
    if (action == "view") {
      setViewDoc({ ...data, url: link.href });
    } else {
      link.click();
      dispatch(
        setAlertData({
          alertMessage: `${documentName} is downloading...`,
          alertVariant: "success",
          openAlert: true,
        })
      );
    }
    return true;
  };

  /**
   * Pulls the document straight from the EHR rather than the CareVitality store.
   *
   * Offered wherever the vendor can serve the bytes: an eCW DocumentReference id, or an
   * Athena clinical document, whose PDF Athena keeps rather than sending during sync.
   *
   * `silent` is set by the merged download handler below while a CareVitality-copy fallback
   * is still available, so a vendor-side failure (e.g. the metadata synced but the file was
   * never actually uploaded to the EHR) does not surface an alert the fallback is about to
   * make moot.
   */
  const handleEhrDownload = async (
    doc: CatSearchDocType,
    action: "view" | "download" = "download",
    silent = false
  ): Promise<boolean> => {
    const res = await EhrDocumentApi.downloadFromEhr(
      patientOrg?.practiceLocationId,
      patientInfo.id,
      doc.id,
      ehr?.vendor
    );
    if (!res.success || !res.blob) {
      if (!silent) {
        dispatch(
          setAlertData({
            alertMessage: res.message || "Could not download from the EHR.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
      return false;
    }
    if (!res.blob.size) {
      if (!silent) {
        dispatch(setAlertData({ alertMessage: "The EHR returned an empty document.", alertVariant: "error", openAlert: true }));
      }
      return false;
    }
    const pdfBlob = new Blob([await res.blob.arrayBuffer()], {
      type: res.contentType?.includes("pdf") || doc.fileType === ".pdf"
        ? "application/pdf"
        : res.contentType || "application/octet-stream",
    });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(pdfBlob);
    const originalName = doc.documentName || `${doc.id}`;
    const extension = res.contentType === "application/pdf" ? ".pdf" : "";
    link.download = extension && !originalName.toLowerCase().endsWith(extension)
      ? `${originalName.replace(/\\.pdf(?=\\s)/i, "")}${extension}`
      : originalName;
    if (action === "view") {
      setViewDoc({ ...doc, url: link.href });
      return true;
    }
    link.click();
    window.URL.revokeObjectURL(link.href);
    dispatch(
      setAlertData({
        alertMessage: `${doc.documentName} downloaded from the EHR.`,
        alertVariant: "success",
        openAlert: true,
      })
    );
    return true;
  };

  /**
   * The one download/view action offered per document, regardless of where its bytes live.
   *
   * Previously two separate buttons ("Download CareVitality copy" and "Download EHR source")
   * left the user to guess which one would actually work for a given document. This tries the
   * EHR first when the document is EHR-sourced -- consistent with the eye/"view" action above,
   * which already prefers the EHR as the fresher copy -- and falls back to the CareVitality copy
   * if that fails and one exists, so "wherever the document is present" it comes down with one
   * click instead of two possible dead ends.
   */
  const handleDownload = async (
    doc: CatSearchDocType,
    action: "view" | "download" = "download"
  ): Promise<void> => {
    const hasLocalCopy = doc.availability === "Available";
    const isEhrSourced = isEhrSourcedDocument(
      patientOrg?.practiceLocationId,
      doc.id,
      ehr?.vendor
    );
    if (isEhrSourced) {
      const ehrSucceeded = await handleEhrDownload(doc, action, hasLocalCopy);
      if (ehrSucceeded || !hasLocalCopy) return;
    }
    if (hasLocalCopy) {
      await handleClick(doc, action);
    }
  };

  const SubContent: FC<{ data: RedoxCategoryType }> = ({ data }) => {
    return (
      <Fragment>
        {data.docList.map((docEl) => {
          return (
            <Box
              display="flex"
              alignItems="center"
              key={docEl.id}
              sx={{ minWidth: 0, gap: 0.5 }}
            >
              {/* Name and date share one column: Athena names documents by type, so hundreds of
                  rows carry the same file name and the date is the only thing that tells them
                  apart. Without it the list reads as one document repeated (CV-468). */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  title={docEl.documentName}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {docEl.documentName}
                </Typography>
                {documentSubtitle(docEl) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    title={documentSubtitle(docEl)}
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {documentSubtitle(docEl)}
                  </Typography>
                )}
              </Box>
              <Box display="flex" justifyContent="flex-end" flexShrink={0}>
                {/* A PDF is a PDF by its content type, not by the extension we guessed for it.
                    Athena hands documents back as application/pdf while the file name ends .png or
                    carries no usable extension, and those lost their view icon entirely -- on
                    patient 84cf098a, 4 of 36 PDFs. The fileType test is kept alongside so nothing
                    that opens today stops opening. */}
                {(docEl.contentType === "application/pdf" ||
                  docEl.fileType == ".pdf") && (
                  <IconButton
                    sx={{ p: 0, mr: 1 }}
                    onClick={() => handleDownload(docEl, "view")}
                  >
                    <ImgPath.RemoveRedEyeOutlined fontSize="small" />
                  </IconButton>
                )}
                {/* One button regardless of where the bytes actually live -- handleDownload tries
                    the EHR first when the document is EHR-sourced and falls back to the
                    CareVitality copy if that fails and one exists, so the button is offered
                    whenever either source might serve it. */}
                {(docEl.availability === "Available" ||
                  isEhrSourcedDocument(
                    patientOrg?.practiceLocationId,
                    docEl.id,
                    ehr?.vendor
                  )) && (
                  <Tooltip arrow placement="top" title="Download document">
                    <IconButton
                      size="small"
                      sx={{ p: 0.5, ml: 0.5 }}
                      onClick={() => handleDownload(docEl, "download")}
                    >
                      <ImgPath.DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Fragment>
    );
  };

  return (
    <Box sx={{ ...sxMain }}>
      {loading ? (
        <Loader />
      ) : (
        data.map((el) => {
          return (
            <CstmAccordion1
              key={el.id}
              mainContent={
                <Box
                  sx={{ ...CommonStyle.sxRow, justifyContent: "space-between" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {el.label}
                  </Typography>
                  <Typography variant="subtitle1" mr={1}>
                    {el.count || 0}
                  </Typography>
                </Box>
              }
              subContent={<SubContent data={el} />}
            />
          );
        })
      )}
    </Box>
  );
};
export default Category;

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};
